import { formatDateTime, toPayloadDateBroker } from '@energy-stacks/shared';
import CardWidget from '../CardWidget';
import { useGetUptimeTimeSlicesQuery } from '@energy-stacks/broker/feature-dashboard-data';
import { Props } from 'react-apexcharts';
import { IconArrowBigUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

interface UptimeWidgetProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export const UptimeWidget = ({ startDate, endDate }: UptimeWidgetProps) => {
  const { t } = useTranslation('dashboard');

  const { data, isLoading } = useGetUptimeTimeSlicesQuery(
    {
      from: startDate ? toPayloadDateBroker(startDate) : undefined,
      to: endDate ? toPayloadDateBroker(endDate) : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const totalUptime = data?.reduce((sum, slice) => sum + slice.uptime, 0) ?? 0;
  const averageUptime = data && data.length > 0 ? totalUptime / data.length : 0;

  const plottedUptimeRate =
    data?.map((slice) => {
      const uptimeRate = slice?.uptime;
      const dateSlice = slice.timesliceStart;

      return {
        rate: Math.round(uptimeRate),
        timestamp: formatDateTime(dateSlice),
      };
    }) || [];

  const chartData: Props = {
    type: 'line',
    height: 90,
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        sparkline: {
          enabled: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        padding: {
          top: 7,
          bottom: 7,
          left: 7,
          right: 7,
        },
      },
      colors: ['#fff'],
      fill: {
        type: 'solid',
        opacity: 1,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      yaxis: {
        min: 0,
        max: 100,
      },
      tooltip: {
        theme: 'dark',
        fixed: {
          enabled: false,
        },
        x: {
          show: true,
        },
        y: {
          title: {
            formatter: (seriesName: string) => seriesName,
          },
          formatter: (val: number) => val + '%',
        },
        marker: {
          show: false,
        },
      },
    },
    series: [
      {
        name: t('connectorUptime'),
        data: plottedUptimeRate.map((uptimeRate) => {
          return {
            x: uptimeRate.timestamp,
            y: uptimeRate.rate,
          };
        }),
      },
    ],
  };

  return (
    <Box>
      <CardWidget
        chartData={chartData}
        color="green"
        isLoading={isLoading}
        value={Math.round(averageUptime) + '%'}
        name={t('connectorUptime')}
        icon={<IconArrowBigUp />}
      />
    </Box>
  );
};
